import { Grid, Typography } from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { TransactionSearchContext } from 'src/common/interfaces/states/ITransactionSearchState';
import { reprintLastReceipt } from 'src/reprint-receipt/business-logic/handle-reprint-receipt';
import BaseButton from '../common/components/base-button';
import { restorePage, setHomePage } from '../common/components/emerald-home-page/emerald-home-page';
import { IButton } from '../common/interfaces/IButton';
import { POSModeState } from '../common/interfaces/states/POSModeState';
import { AppState } from '../common/redux/configure-store';
import { trainingModeStyles } from '../common/training-mode/training-mode-styles';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';
import { ScannedDataHandler } from '../devices/scanner-operations';
import Keypad from '../keypad/components/keypad';
import { transactionSearchModalStyles } from './transaction-based-return-styles';

type Props = ISearchTransactionProps;

export const showSearchTransactionPage = (props: Props, onScan: ScannedDataHandler): void => {
  setHomePage(<SearchTransactionPage {...props} />, onScan);
};

export const closeSearchTransactionPage = (): void => restorePage();

const SearchTransactionPage: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = transactionSearchModalStyles();
  const { t } = useTranslation();
  const trainingModeClasses = trainingModeStyles();
  const leftHanded = useSelector((state: AppState): boolean => state.cashierPreferences.leftHanded);
  const posModeState = useSelector((state: AppState): POSModeState => state.posMode);
  const searchContext = useSelector(
    (state: AppState): TransactionSearchContext => state.transactionSearch.searchContext,
  );

  let titleText, altButtonText, testIdPrefix, altButtonPrimaryColor, onAltButtonClick;
  switch (searchContext) {
    case TransactionSearchContext.TBR: {
      testIdPrefix = 'transaction-search';
      titleText = 'Find Transaction';
      altButtonPrimaryColor = false;
      break;
    }
    case TransactionSearchContext.ReprintReceipt: {
      testIdPrefix = 'reprint-receipt';
      titleText = 'ReprintCommand';
      altButtonText = 'ReprintLastTransaction';
      altButtonPrimaryColor = true;
      onAltButtonClick = reprintLastReceipt;
      break;
    }
  }

  const keypadSubmitButton: IButton = {
    label: t('SEARCH'),
    onClick: props.onKeypadSubmit,
    testId: `${testIdPrefix}-search-keypad-button`,
  };

  return (
    <Grid item className={`fullHeight flexFull`}>
      <Grid
        item
        className={`${
          posModeState.trainingMode
            ? trainingModeClasses.trainingModeBackground
            : classes.background
        } fullWidth fullHeight`}
        data-testid='find-transaction-screen-container'
      >
        {posModeState.trainingMode && (
          <TrainingModeWatermark supplementalStyles={trainingModeClasses.centerTradeWaterMark} />
        )}
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.returnItemContainer} ${classes.outerFlexDesgin}`}
        >
          <Grid container direction='column' justifyContent='center' className={`fullWidth`}>
            <Grid item className={classes.fitHeight}>
              <Grid container direction='column' className={`fullWidth`}>
                <Typography
                  variant='h2'
                  className={`${classes.returnItemTitleHeader}`}
                  data-testid='find-transaction-title-container'
                >
                  {t(`${titleText}`)}
                </Typography>
                <Grid item>
                  <Typography className={`${classes.returnItemTitleBar}`} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            container
            className={classes.flexDesign}
            direction={leftHanded ? 'row-reverse' : 'row'}
          >
            <Grid item container xs={9} className={classes.labelHeadTop}>
              <Grid item container className={classes.containerAlignment}>
                <Grid item>
                  <Typography
                    className={`${classes.returnItemTitleHeaderInside} ${classes.codeContainerSubHeaderLabel} `}
                    data-testid={`${testIdPrefix}-first-helper-text`}
                  >
                    {t('EnterReceiptBarcodeOr')}
                  </Typography>
                  <Typography
                    className={`${classes.returnItemTitleHeaderInside} ${classes.codeContainerSubHeaderLabel}`}
                    data-testid={`${testIdPrefix}-second-helper-text`}
                  >
                    {t('LoyaltyCard')}
                  </Typography>
                  <Grid container justifyContent='center'>
                    <Typography
                      className={`${classes.orSection} ${classes.codeContainerSubHeaderLabel}`}
                    >
                      - {t('ScanItemBarcodeOrText')} -
                    </Typography>
                  </Grid>
                  {altButtonText && (
                    <Grid container justifyContent='center'>
                      <BaseButton
                        buttonType={altButtonPrimaryColor ? 'primary' : 'white'}
                        className={classes.searchByTransactionDetailsButton}
                        onClick={onAltButtonClick}
                        data-testid={`${testIdPrefix}-alt-search-by-criteria-button`}
                      >
                        {t(`${altButtonText}`)}
                      </BaseButton>
                    </Grid>
                  )}
                  <Grid container justifyContent='center'>
                    <BaseButton
                      buttonType={!altButtonPrimaryColor ? 'primary' : 'white'}
                      className={classes.searchByTransactionDetailsButton}
                      onClick={props.completeTransactionSearch}
                      data-testid={`${testIdPrefix}-search-by-criteria-button`}
                    >
                      {t('SearchByTransactionDetails')}
                    </BaseButton>
                  </Grid>
                </Grid>
                <Grid item className={classes.cancelButton}>
                  <BaseButton
                    buttonType='white'
                    className={classes.actionButtons}
                    onClick={props.onClose}
                    data-testid={`${testIdPrefix}-cancel-button`}
                  >
                    {t('Cancel')}
                  </BaseButton>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={3} className={classes.keypadContainer}>
              <Keypad
                visible
                keyboardId={testIdPrefix}
                inputFieldText={''}
                keypadTestIdPrefix={`${testIdPrefix}-item-`}
                topLabelText={
                  searchContext === TransactionSearchContext.TBR
                    ? t('Keypad_Receipt_Return')
                    : t('')
                }
                submitButton={keypadSubmitButton}
                disableDecimalInput
                disableManualWeight
                disableQuantity
                disableSubmitWhenNoInput
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SearchTransactionPage;

export interface ISearchTransactionProps {
  onKeypadSubmit: (itemCode: string) => Promise<void>;
  onClose: () => void;
  completeTransactionSearch: () => void;
}
